import { useClientProduct, useCreateOrder } from "api/Client/product";
import { Spinner } from "components";
import CustomInput from "components/ClientLayoutPages/ClientInputField/CustomInput";
import Container from "components/ClientLayoutPages/Container/Container";
import MyOrderModal from "components/Modal/Client/MyOrderModal/MyOrderModal";
// kossay start: استيراد نافذة الأرقام المحفوظة
import SavedNumbersModal from "components/Modal/Client/SavedNumbersModal/SavedNumbersModal";
// kossay end
import useParam from "Hooks/useParam";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  HiOutlineCheckCircle,
  HiOutlineClock,
  HiOutlineCube,
  // kossay start: أيقونة سجل الهاتف
  HiOutlineIdentification,
  // kossay end
  HiOutlineShoppingBag,
  HiOutlineXCircle,
} from "react-icons/hi2";
import { useSelector } from "react-redux";

// kossay start: المنتجات التي تظهر فيها أيقونة الأرقام المحفوظة
// المفتاح = productId (غيّره إلى الـ id الموجود في السيرفر قبل الرفع)
// type = نوع السجل، fill = fieldId للحقول التي تُملأ من السجل (الكمية لا تُملأ)
const SAVED_NUMBERS_PRODUCTS = {
  3: { type: "syriatel", fill: ["number"] }, // جملة سيرتيل
  4: { type: "mtn", fill: ["number", "code"] }, // جملة MTN
};
// kossay end

function SummaryCard({
  icon,
  title,
  value,
  accent = "portal-accent",
  ltr = false,
}) {
  return (
    <div className="portal-page-card flex items-center gap-3 rounded-[1.35rem] p-4">
      <div className={`text-2xl ${accent}`}>{icon}</div>
      <div className="min-w-0">
        <p className="text-xs font-semibold portal-accent">{title}</p>
        <p
          className="truncate text-sm font-bold sm:text-base"
          style={{
            color: "var(--layout-text)",
            direction: ltr ? "ltr" : "rtl",
          }}
        >
          {value}
        </p>
      </div>
    </div>
  );
}

export default function Product() {
  const { currency } = useSelector((x) => x.balance);
  const productId = useParam("productId");
  const [fields, setFields] = useState([]);
  const [fieldsValue, setFieldsValue] = useState({});
  const [price, setPrice] = useState(0);
  const [total, setTotal] = useState(0);
  const [showConfirmButtons, setShowConfirmButtons] = useState(false);
  const [timer, setTimer] = useState(10);
  const [lastCreatedOrderId, setLastCreatedOrderId] = useState(null);
  const countdownRef = useRef(null);

  // kossay start: حالة نافذة الأرقام المحفوظة + إعداد المنتج الحالي
  const [savedNumbersOpen, setSavedNumbersOpen] = useState(false);
  const savedNumbersConfig = SAVED_NUMBERS_PRODUCTS[productId];
  // kossay end

  const {
    data: product,
    isLoading: productIsLoading,
    isSuccess: productIsSuccess,
  } = useClientProduct(productId);

  const {
    mutate: createOrder,
    isPending: createOrderIsPending,
    isSuccess: createOrderIsSuccess,
    reset: resetCreateOrderMutation,
  } = useCreateOrder(productId, {
    navigateAfterCreate: false,
    onCreated: (orderId) => setLastCreatedOrderId(orderId),
  });

  const closeOrderSummaryModal = useCallback(
    (open) => {
      if (open === false) {
        setLastCreatedOrderId(null);
        resetCreateOrderMutation();
      }
    },
    [resetCreateOrderMutation],
  );

  useEffect(() => {
    if (productIsSuccess) {
      setFields(JSON.parse(product.data.product?.fields || "[]") || []);
      setPrice(product.data.product?.price || 0);
    }
  }, [productIsSuccess, product]);

  useEffect(() => {
    if (product?.data?.product?.is_quantity) {
      setTotal(fieldsValue?.quantity * price || 0);
    } else {
      setTotal(price || 0);
    }
  }, [fieldsValue, price, product?.data?.product?.is_quantity]);

  useEffect(() => {
    if (createOrderIsSuccess) {
      setFieldsValue((prev) =>
        Object.keys(prev).reduce((acc, key) => {
          acc[key] = "";
          return acc;
        }, {}),
      );
      setTotal(0);
      setShowConfirmButtons(false);
      setTimer(10);
    }
  }, [createOrderIsSuccess]);

  useEffect(() => {
    return () => {
      clearInterval(countdownRef.current);
    };
  }, []);

  const startCountdown = () => {
    setShowConfirmButtons(true);
    setTimer(10);
    clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setTimer((prev) => {
        if (prev === 1) {
          clearInterval(countdownRef.current);
          handleConfirmOrder();
          return 10;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleConfirmOrder = () => {
    clearInterval(countdownRef.current);
    setShowConfirmButtons(false);
    createOrder(fieldsValue);
  };

  // kossay start: ملء الحقول من السجل المختار (بدون حقل الكمية)
  const handleSelectSavedNumber = (record) => {
    if (!savedNumbersConfig) return;
    setFieldsValue((prev) => {
      const next = { ...prev };
      savedNumbersConfig.fill.forEach((fieldId) => {
        next[fieldId] = record[fieldId] ?? "";
      });
      return next;
    });
    setSavedNumbersOpen(false);
  };
  // kossay end

  const handleCancel = () => {
    clearInterval(countdownRef.current);
    setShowConfirmButtons(false);
    setTimer(10);
  };

  const formattedTotal =
    currency === "USD"
      ? Math.ceil(total * 1000) / 1000
      : currency === "S.P"
        ? parseFloat(total || 0).toLocaleString()
        : "";

  if (productIsLoading) return <Spinner page />;
  if (!productIsSuccess) return null;

  const currentProduct = product.data.product;

  return (
    <Container
    // title={currentProduct.name}
    // message={product.data.product_full_path}
    // back
    >
      <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-3">
        <SummaryCard
          icon={<HiOutlineShoppingBag />}
          title={product.data.product_full_path}
          value={currentProduct.category_name || currentProduct.name}
              accent="text-orange-400"
        />
        <SummaryCard
          icon={<HiOutlineCube />}
          title="السعر الأساسي"
          value={`${Number(price || 0).toLocaleString()} ${currency}`}
              accent="text-orange-300"
          ltr
        />
        <SummaryCard
          icon={
            currentProduct.is_available === 1 ? (
              <HiOutlineCheckCircle />
            ) : (
              <HiOutlineXCircle />
            )
          }
          title="حالة المنتج"
          value={
            currentProduct.is_available === 1
              ? "متوفر الآن"
              : "غير متوفر حالياً"
          }
          accent={
            currentProduct.is_available === 1
              ? "text-emerald-400"
              : "text-rose-400"
          }
        />
      </div>
      {currentProduct?.description !== "<p><br></p>" &&
      currentProduct?.description != null ? (
        <div
          className="portal-page-card mb-4 rounded-[1.5rem] p-5 leading-7 !bg-[linear-gradient(135deg,rgba(255,178,91,0.22),rgba(255,139,31,0.14))] dark:!bg-transparent"
          style={{ color: "var(--layout-text)" }}
          dangerouslySetInnerHTML={{
            __html: currentProduct.description,
          }}
        />
      ) : null}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          startCountdown();
        }}
        className="portal-page-card grid grid-cols-1 gap-5 rounded-[1.5rem] p-4 md:p-5"
      >
        {/* kossay start: زر أرقامي المحفوظة (سيرتيل و MTN فقط) */}
        {savedNumbersConfig ? (
          <button
            type="button"
            className="portal-outline-button w-full rounded-xl px-4 py-3"
            onClick={() => setSavedNumbersOpen(true)}
          >
            <span className="inline-flex items-center justify-center gap-2">
              <HiOutlineIdentification className="text-xl" />
              أرقامي المحفوظة
            </span>
          </button>
        ) : null}
        {/* kossay end */}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {fields.map((field) => (
            <CustomInput
              key={field.fieldId}
              id={field.fieldId}
              title={field.name}
              type={field.type}
              max={field.max}
              min={field.min}
              options={field.options}
              value={fieldsValue[field.fieldId] || ""}
              onChange={(v) =>
                setFieldsValue((prev) => ({
                  ...prev,
                  [field.fieldId]: v.value,
                }))
              }
              direction="ltr"
              required
            />
          ))}

          <CustomInput
            id="total"
            title="السعر"
            value={formattedTotal}
            isDisabled
            direction="ltr"
          />
        </div>

        <div className="portal-page-card-soft rounded-[1.25rem] px-4 py-3">
          <div
            className="flex items-center gap-2 text-sm"
            style={{ color: "var(--layout-text-muted)" }}
          >
                    <HiOutlineClock className="text-lg text-orange-400" />
            سيتم عرض تأكيد قبل تنفيذ الشراء، ويمكنك الإلغاء خلال العد التنازلي.
          </div>
        </div>

        {currentProduct.is_available === 0 ? (
          <button
            className="portal-outline-button mx-auto mt-2 w-full max-w-xs rounded-xl px-4 py-3 opacity-60"
            disabled
            type="button"
          >
            هذا المنتج غير متوفر حالياً
          </button>
        ) : !showConfirmButtons ? (
          <button
            className="portal-outline-button mx-auto mt-2 w-full max-w-xs rounded-xl px-4 py-3"
            disabled={createOrderIsPending}
            type="submit"
          >
            {createOrderIsPending ? <Spinner sm /> : "شراء"}
          </button>
        ) : (
          <div className="mx-auto mt-2 flex w-full max-w-xl flex-col gap-3 sm:flex-row sm:justify-center">
            <button
              type="button"
              className="portal-outline-button w-full rounded-xl px-4 py-3 sm:w-44"
              onClick={handleConfirmOrder}
              disabled={createOrderIsPending}
            >
              تأكيد الشراء ({timer})
            </button>
            <button
              type="button"
              className="rounded-xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 font-bold text-rose-300 transition hover:bg-rose-500/20 sm:w-44"
              onClick={handleCancel}
            >
              إلغاء
            </button>
          </div>
        )}
      </form>

      {/* kossay start: نافذة الأرقام المحفوظة */}
      {savedNumbersConfig && savedNumbersOpen ? (
        <SavedNumbersModal
          isOpen={savedNumbersOpen}
          toggle={setSavedNumbersOpen}
          productType={savedNumbersConfig.type}
          onSelect={handleSelectSavedNumber}
        />
      ) : null}
      {/* kossay end */}

      {lastCreatedOrderId != null && lastCreatedOrderId !== "" ? (
        <MyOrderModal
          isOpen={!!lastCreatedOrderId}
          toggle={closeOrderSummaryModal}
          orderId={lastCreatedOrderId}
        />
      ) : null}
    </Container>
  );
}
